import { useRef } from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { notificationPreferencesQuery } from "../queries";
import { setNotificationPreferenceCategoryMutation } from "../commands";
import type { ListNotificationPreferencesResponse, NotificationPreferenceItem } from "../../client-messages";
import {
  DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
  type NotificationCategory,
  type NotificationChannel,
  type NotificationChannelPreferences,
  type NotificationPreferenceScope,
} from "../../preferences";

function scopesEqual(a: NotificationPreferenceScope, b: NotificationPreferenceScope): boolean {
  if (a.kind === "allCameras" && b.kind === "allCameras") return true;
  if (a.kind === "site" && b.kind === "site") return a.siteId === b.siteId;
  if (a.kind === "camera" && b.kind === "camera") {
    return a.siteId === b.siteId && a.assetId === b.assetId && a.cameraId === b.cameraId;
  }
  return false;
}

/** A stable string key for {@link scopesEqual}'s equivalence classes — used to key the per-scope+category write queue. */
function scopeCategoryKey(scope: NotificationPreferenceScope, category: NotificationCategory): string {
  return `${JSON.stringify(scope)}::${category}`;
}

function readScopeCategoryChannels(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: NotificationPreferenceScope,
  category: NotificationCategory,
): NotificationChannelPreferences {
  const list = queryClient.getQueryData<ListNotificationPreferencesResponse>(queryKey)?.preferences ?? [];
  const existing = list.find((item) => scopesEqual(item.scope, scope));
  return existing?.preferences[category] ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES[category];
}

/** The next scope up the wildcard chain — `undefined` for `allCameras`, which has nothing broader. */
function broaderScope(scope: NotificationPreferenceScope): NotificationPreferenceScope | undefined {
  switch (scope.kind) {
    case "camera":
      return { kind: "site", siteId: scope.siteId };
    case "site":
      return { kind: "allCameras" };
    case "allCameras":
      return undefined;
  }
}

function isAllOff(channels: NotificationChannelPreferences): boolean {
  return !channels.email && !channels.sms && !channels.push;
}

/**
 * The channels a scope+category is *currently* firing under — its own row if
 * one exists, else its broader scope's (cascading site → allCameras, matching
 * `NotificationPreferenceScope`'s documented resolution order), else all off.
 * This is what the UI shows as checked (see `NotificationPreferenceGroup`'s
 * `inherited`) even when nothing is actually stored for this exact scope yet.
 *
 * `list` merges each scope's two categories into one item (see
 * `getNotificationPreferences`), defaulting whichever category has no row to
 * all-off — so an item existing for this scope does NOT mean *this category*
 * has an explicit row; it may just be the other category's row, merged with
 * this one's default. An all-off triple is treated as "no explicit row" and
 * cascades rather than being read as-is: this is safe (not just an
 * approximation) only because `NotificationPreferencesService` deletes a row
 * the instant every channel goes off, so a real explicit row can never
 * actually be all-off — if this ever changes, this function must too.
 */
function effectiveScopeCategoryChannels(
  list: readonly NotificationPreferenceItem[],
  scope: NotificationPreferenceScope,
  category: NotificationCategory,
): NotificationChannelPreferences {
  const own = list.find((item) => scopesEqual(item.scope, scope))?.preferences[category];
  if (own !== undefined && !isAllOff(own)) return own;

  const broader = broaderScope(scope);
  return broader ?
      effectiveScopeCategoryChannels(list, broader, category)
    : DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES[category];
}

/**
 * Writes one scope+category's channels into the cached list, leaving every
 * other entry (including ones written by a concurrent call for a different
 * scope+category) untouched — used for both the optimistic write and a
 * same-call rollback, so neither can clobber an unrelated write.
 */
function writeScopeCategoryChannels(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: NotificationPreferenceScope,
  category: NotificationCategory,
  channels: NotificationChannelPreferences,
): void {
  queryClient.setQueryData<ListNotificationPreferencesResponse>(queryKey, (old) => {
    const list = old?.preferences ?? [];
    const existing = list.find((item) => scopesEqual(item.scope, scope));
    const updated: NotificationPreferenceItem = {
      scope,
      preferences: { ...(existing?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES), [category]: channels },
    };
    return {
      preferences:
        existing ? list.map((item) => (scopesEqual(item.scope, scope) ? updated : item)) : [...list, updated],
    };
  });
}

export interface NotificationPreferencesState {
  /** Every scope the user has saved a preference for — raw, unmerged with the tenant's site/camera list. */
  readonly preferences: readonly NotificationPreferenceItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  /**
   * Flips one channel within one scope+category, replacing that category's
   * whole channel triple on the server (see `NotificationPreferencesService`) —
   * the other two channels are read fresh from the cache rather than trusted
   * from the caller, so this never needs a pre-built triple. Call once per
   * toggle, immediately, rather than batching several toggles behind a "Save"
   * button.
   *
   * The untouched channels are carried forward from what's *effectively* on —
   * this scope's own row if it has one, else cascading up to a broader scope's
   * (see `NotificationPreferenceScope`'s resolution order) — not from this
   * scope's own row alone. A row for this scope is a full override the moment
   * it exists, so building it from an all-off default would silently drop a
   * channel this scope only had via inheritance (e.g. toggling SMS on for a
   * site whose Email is only on because "All Cameras" Email is on must still
   * write Email as on for that site, not false).
   *
   * Writes for the same scope+category are serialized (a second call waits for
   * the first to finish applying before it reads "current") so two quick
   * toggles on the same row compose instead of the second one clobbering the
   * first with a stale snapshot. A failure only rolls back its own write — the
   * specific scope+category's previous channels, not a whole-list snapshot —
   * so it can't undo an unrelated write that landed in between.
   */
  readonly setCategory: (
    scope: NotificationPreferenceScope,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => Promise<void>;
}

/**
 * Raw API wiring for self-service notification preferences — deliberately has
 * no knowledge of the tenant's actual sites/cameras (names, which ones exist).
 * A caller merges `preferences` against its own site/camera list to render a
 * complete settings screen (a scope with no saved row here just means "not
 * configured yet", not "doesn't exist") — kept out of this feature since that
 * list comes from provisioning, not alarm-schedules (ADR-0026).
 */
export function useNotificationPreferences(): NotificationPreferencesState {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(notificationPreferencesQuery(apiConfig, {}));

  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  const setCategoryMutation = useMutation(setNotificationPreferenceCategoryMutation(mutationConfig, {}));

  // Chains writes per scope+category key so a second toggle on the same row
  // only starts once the first has applied its optimistic write — see the
  // `setCategory` doc comment.
  const pendingWrites = useRef(new Map<string, Promise<void>>()).current;

  const applyChannelChange = async (
    scope: NotificationPreferenceScope,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ): Promise<void> => {
    const queryOptions = notificationPreferencesQuery(apiConfig, {});
    // What's actually stored for this exact scope+category before this write —
    // possibly nothing — used to roll the cache back to what it really held.
    const previousOwnChannels = readScopeCategoryChannels(queryClient, queryOptions.queryKey, scope, category);
    // What's *effectively* on right now (this scope's own row, or a broader
    // scope's if this one has none yet) — the untouched channels must be built
    // from this, not from `previousOwnChannels`. A narrower row is a full
    // override once it exists (see `NotificationPreferenceScope`'s doc
    // comment), so writing this scope's own all-false default for a channel
    // that's only ever been on via inheritance would silently turn it off.
    const list =
      queryClient.getQueryData<ListNotificationPreferencesResponse>(queryOptions.queryKey)?.preferences ?? [];
    const effectiveChannels = effectiveScopeCategoryChannels(list, scope, category);
    const channels: NotificationChannelPreferences = { ...effectiveChannels, [channel]: enabled };

    // Applied eagerly so the checkbox reflects the change immediately, and left
    // in place (not refetched) on success — it already exactly matches what the
    // server just accepted, and this query has no other consumer to reconcile
    // for. Refetching here would race a *different* scope+category's own
    // still-in-flight optimistic write (writes are only serialized within the
    // same scope+category — see `setCategory`), snapping its checkbox back
    // until that other request finishes. Rolled back below if this write fails.
    writeScopeCategoryChannels(queryClient, queryOptions.queryKey, scope, category, channels);

    try {
      await setCategoryMutation.mutateAsync({ scope, category, channels });
    } catch (error) {
      writeScopeCategoryChannels(queryClient, queryOptions.queryKey, scope, category, previousOwnChannels);
      throw error;
    }
  };

  const setCategory = (
    scope: NotificationPreferenceScope,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ): Promise<void> => {
    const key = scopeCategoryKey(scope, category);
    const queued = (pendingWrites.get(key) ?? Promise.resolve()).then(() =>
      applyChannelChange(scope, category, channel, enabled),
    );
    // The map only ever needs to remember that something is pending, not its
    // outcome — swallow here so a failed write doesn't wedge the queue for
    // this key; the caller below still observes the rejection via `queued`.
    pendingWrites.set(
      key,
      queued.then(
        () => undefined,
        () => undefined,
      ),
    );
    return queued;
  };

  return {
    preferences: data?.preferences ?? [],
    isLoading,
    isError,
    setCategory,
  };
}
